import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { LoginPage } from "@/auth/LoginPage";
import { RequireAuth } from "./RequireAuth";
import { RequireStaff } from "./RequireStaff";
import { AppShell } from "./AppShell";

// Route-split the heavier views so the initial load (login/shell) doesn't ship the code
// editor (CodeMirror) or admin bundles. Each page becomes its own lazily-fetched chunk.
const HomePage = lazy(() => import("./HomePage").then((m) => ({ default: m.HomePage })));
const CreatePage = lazy(() => import("./CreatePage").then((m) => ({ default: m.CreatePage })));
const ProjectsPage = lazy(() =>
  import("@/projects/ProjectsPage").then((m) => ({ default: m.ProjectsPage })),
);
const EditorPage = lazy(() => import("@/editor/EditorPage").then((m) => ({ default: m.EditorPage })));
const RosterPage = lazy(() => import("@/admin/RosterPage").then((m) => ({ default: m.RosterPage })));
const StudentProjectsPage = lazy(() =>
  import("@/admin/StudentProjectsPage").then((m) => ({ default: m.StudentProjectsPage })),
);
const PublicUserPage = lazy(() => import("./PublicPage").then((m) => ({ default: m.PublicUserPage })));
const PublicProjectPage = lazy(() =>
  import("./PublicPage").then((m) => ({ default: m.PublicProjectPage })),
);

function PageFallback() {
  return (
    <div className="flex h-full items-center justify-center bg-ide-dark text-ide-offwhite/60">
      Loading…
    </div>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageFallback />}>
      <Routes>
        {/* Share-link URLs — preserved byte-for-byte from legacy */}
        <Route path="/public/:username" element={<PublicUserPage />} />
        <Route path="/public/:username/:projectID" element={<PublicProjectPage />} />

        <Route path="/login" element={<LoginPage />} />

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route path="/" element={<HomePage />} />
          <Route path="/create" element={<CreatePage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/editor/:projectId" element={<EditorPage />} />
          <Route
            path="/admin"
            element={
              <RequireStaff>
                <RosterPage />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/students/:uid"
            element={
              <RequireStaff>
                <StudentProjectsPage />
              </RequireStaff>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
